import { Sparkles } from 'lucide-react';

// Editorial title treatment — first half white, second half signature teal.
// Same language as ZenFlow's ImmersivePost.
export function SplitTitle({ text }) {
  const words = (text || '').split(/\s+/).filter(Boolean);
  if (words.length < 3) return <>{text}</>;
  const cut = Math.ceil(words.length / 2);
  return (
    <>
      {words.slice(0, cut).join(' ')}{' '}
      <span className="text-[#5EEAD4]">{words.slice(cut).join(' ')}</span>
    </>
  );
}

/**
 * ZenTile — the global ZenScroll content canvas.
 * One presentation language across Home, Explore, Trending, Community, Saved:
 *   - hard 90° corners (no rounded-card styling)
 *   - media fills the canvas (children render inside the aspect frame)
 *   - subtle bottom scrim, title + @user bottom-left, action rail on the right
 * `actions`: [{ key, label, Icon, onClick, accent }] or { key, node } for
 * pre-built controls (LikeButton / BookmarkButton).
 */
export default function ZenTile({
  testId,
  aspect = 'aspect-[4/5]',
  className = '',
  title,
  caption,
  username,
  aiCreated = false,
  onOpen,
  onUserClick,
  actions = [],
  topLeft,
  topRight,
  children,
}) {
  return (
    <article
      data-testid={testId}
      className={`group relative overflow-hidden bg-[#0A1514] ${className}`}
      style={{ borderRadius: 0 }}
    >
      <div
        className={`relative ${aspect} w-full overflow-hidden bg-[#0A1514] ${onOpen ? 'cursor-pointer' : ''}`}
        onClick={onOpen}
        role={onOpen ? 'button' : undefined}
        tabIndex={onOpen ? 0 : undefined}
        onKeyDown={onOpen ? (e) => { if (e.key === 'Enter' && e.target === e.currentTarget) onOpen(); } : undefined}
      >
        {children}
      </div>

      <div className="absolute inset-0 pointer-events-none">
        {/* Subtle readability gradient — fades into the artwork */}
        <div className="zs-scrim-bottom" />

        {topLeft && <div className="absolute top-3 left-3 pointer-events-auto">{topLeft}</div>}
        {topRight && <div className="absolute top-3 right-3 pointer-events-auto">{topRight}</div>}

        {/* Right-edge interaction rail */}
        {actions.length > 0 && (
          <div className="absolute right-2.5 bottom-4 flex flex-col items-center gap-2.5 pointer-events-auto">
            {actions.map((a) => (a.node ? (
              <span key={a.key}>{a.node}</span>
            ) : (
              <button
                key={a.key}
                type="button"
                data-testid={`${testId}-${a.key}`}
                aria-label={a.label}
                title={a.label}
                onClick={(e) => { e.stopPropagation(); a.onClick?.(e); }}
                className={`inline-flex items-center justify-center w-10 h-10 rounded-full bg-black/40 backdrop-blur-md border transition-colors ${
                  a.accent
                    ? 'text-[#5EEAD4] border-[#5EEAD4]/40 hover:bg-[#5EEAD4]/15'
                    : 'text-white/85 border-white/15 hover:bg-black/60 hover:text-white'
                }`}
              >
                <a.Icon className="w-[17px] h-[17px]" />
              </button>
            )))}
          </div>
        )}

        {/* Title + creator, bottom-left */}
        {(title || caption || username) && (
          <div className={`absolute left-3.5 bottom-3 ${actions.length ? 'right-16' : 'right-4'}`}>
            {title && (
              <h3
                data-testid={`${testId}-title`}
                className="font-display text-lg md:text-xl text-white leading-snug line-clamp-2 drop-shadow-[0_1px_4px_rgba(0,0,0,0.8)]"
              >
                <SplitTitle text={title} />
              </h3>
            )}
            {caption && (
              <p className="mt-1 text-xs md:text-sm text-white/70 italic font-display line-clamp-1 drop-shadow-[0_1px_3px_rgba(0,0,0,0.9)]">
                &ldquo;{caption}&rdquo;
              </p>
            )}
            {username && (
              <p className="mt-1 text-xs text-white/80 flex items-center gap-2">
                {onUserClick ? (
                  <button
                    type="button"
                    data-testid={`${testId}-user`}
                    onClick={(e) => { e.stopPropagation(); onUserClick(); }}
                    className="pointer-events-auto hover:text-[#5EEAD4] transition-colors font-medium drop-shadow-[0_1px_3px_rgba(0,0,0,0.9)]"
                  >
                    @{username}
                  </button>
                ) : (
                  <span className="font-medium drop-shadow-[0_1px_3px_rgba(0,0,0,0.9)]">@{username}</span>
                )}
                {aiCreated && (
                  <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[9px] uppercase tracking-[0.16em] text-[#5EEAD4] border border-[#5EEAD4]/30 bg-black/30 backdrop-blur-sm">
                    <Sparkles className="w-2.5 h-2.5" /> AI Created
                  </span>
                )}
              </p>
            )}
          </div>
        )}
      </div>
    </article>
  );
}
